import {
  Anchor,
  Badge,
  Card,
  Divider,
  Group,
  List,
  Loader,
  Modal,
  ScrollArea,
  Stack,
  Table,
  Text,
} from '@mantine/core';
import type { AdminRoadmapSessionQuestion } from '@prepzio/shared';
import { useSearchParams } from 'react-router-dom';

import { useAdminQuestion } from '../hooks/use-content';
import { useT } from '../i18n';
import { errorMessage } from '../lib/ui';

/**
 * Shared question inspector, used by the Roadmaps and Users explorers:
 *
 * - `useOpenQuestion()` — the open question id lives in `?question=<id>` so it's
 *   deep-linkable and Back closes the modal.
 * - `QuestionDetailModal` — render once per screen; shows the full authored
 *   question (options, expected concepts, hint ladder, example answer, mistakes,
 *   rubric).
 * - `SessionQuestions` — a session's ordered questions with the learner's
 *   answer under each, every prompt clickable to open the modal.
 */

/** Status → Mantine badge color. */
export function statusColor(status: string): string {
  const s = status.toLowerCase();
  if (s === 'completed' || s === 'answered' || s === 'active' || s === 'scored') return 'green';
  if (s === 'skipped' || s === 'expired' || s === 'abandoned') return 'red';
  return 'gray';
}

/** [openQuestionId, setOpenQuestionId] backed by the `?question=` URL param. */
export function useOpenQuestion(): [string | null, (id: string | null) => void] {
  const [params, setParams] = useSearchParams();
  const open = params.get('question');
  const setOpen = (id: string | null) =>
    setParams((prev) => {
      const next = new URLSearchParams(prev);
      if (id) next.set('question', id);
      else next.delete('question');
      return next;
    });
  return [open, setOpen];
}

/** Full question inspector modal, driven by the `?question=` URL param. */
export function QuestionDetailModal() {
  const [openId, setOpenId] = useOpenQuestion();
  const { data: q, isLoading, isError, error } = useAdminQuestion(openId);
  const t = useT();
  return (
    <Modal
      opened={openId != null}
      onClose={() => setOpenId(null)}
      title={t('question.detail')}
      size="lg"
      scrollAreaComponent={ScrollArea.Autosize}
    >
      {isLoading && <Loader size="sm" />}
      {isError && (
        <Text c="red" size="sm">
          {errorMessage(error)}
        </Text>
      )}
      {q && (
        <Stack gap="md">
          <Group gap="xs">
            <Badge variant="light" color="gray">
              {q.type}
            </Badge>
            <Badge variant="light" color="gray">
              {q.difficulty}
            </Badge>
            <Badge variant="light" color={statusColor(q.status)}>
              {q.status}
            </Badge>
            <Badge variant="light" color="gray">
              {q.source}
            </Badge>
            {q.judgeScore != null && (
              <Badge variant="light" color={q.judgeScore >= 0.6 ? 'green' : 'yellow'}>
                {t('question.judge', { score: q.judgeScore.toFixed(2) })}
              </Badge>
            )}
          </Group>

          <Section title={t('question.prompt')}>
            <Text size="sm" style={{ whiteSpace: 'pre-wrap' }}>
              {q.prompt}
            </Text>
          </Section>

          {q.options && q.options.length > 0 && (
            <Section title={t('question.options')}>
              <Stack gap={4}>
                {q.options.map((o) => (
                  <Group key={o.id} gap="xs" wrap="nowrap" align="flex-start">
                    <Badge variant="light" color={o.correct ? 'green' : 'gray'} radius="sm">
                      {o.correct ? '✓' : '·'}
                    </Badge>
                    <Text size="sm">{o.text}</Text>
                  </Group>
                ))}
              </Stack>
            </Section>
          )}

          {q.expectedConcepts.length > 0 && (
            <Section title={t('question.expectedConcepts')}>
              <Group gap={6}>
                {q.expectedConcepts.map((c, i) => (
                  <Badge key={i} variant="light" color="gray" radius="sm">
                    {c}
                  </Badge>
                ))}
              </Group>
            </Section>
          )}

          {q.hints.length > 0 && (
            <Section title={t('question.hintLadder')}>
              <List type="ordered" size="sm" spacing={4}>
                {q.hints.map((h, i) => (
                  <List.Item key={i}>{h}</List.Item>
                ))}
              </List>
            </Section>
          )}

          {q.exampleStrongAnswer && (
            <Section title={t('question.exampleStrongAnswer')}>
              <Text size="sm" style={{ whiteSpace: 'pre-wrap' }}>
                {q.exampleStrongAnswer}
              </Text>
            </Section>
          )}

          {q.commonMistakes.length > 0 && (
            <Section title={t('question.commonMistakes')}>
              <List size="sm" spacing={4}>
                {q.commonMistakes.map((m, i) => (
                  <List.Item key={i}>{m}</List.Item>
                ))}
              </List>
            </Section>
          )}

          {q.rubric && (
            <Section title={t('question.rubric', { title: q.rubric.title })}>
              <Table.ScrollContainer minWidth={360}>
                <Table striped withTableBorder>
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th>{t('question.criterion')}</Table.Th>
                      <Table.Th>{t('question.description')}</Table.Th>
                      <Table.Th>{t('question.max')}</Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {q.rubric.criteria.map((cr) => (
                      <Table.Tr key={cr.key}>
                        <Table.Td>{cr.label}</Table.Td>
                        <Table.Td>{cr.description}</Table.Td>
                        <Table.Td>{cr.maxScore}</Table.Td>
                      </Table.Tr>
                    ))}
                  </Table.Tbody>
                </Table>
              </Table.ScrollContainer>
            </Section>
          )}

          {q.judgeRationale && (
            <>
              <Divider />
              <Text c="dimmed" size="xs">
                {t('question.judgeRationale', { rationale: q.judgeRationale })}
              </Text>
            </>
          )}
        </Stack>
      )}
    </Modal>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div>
      <Text fw={600} size="sm" mb={4}>
        {title}
      </Text>
      {children}
    </div>
  );
}

/**
 * A session's ordered questions with the learner's answer under each. Every
 * prompt is a link that opens the question-detail modal. Render a single
 * `<QuestionDetailModal/>` somewhere on the same screen.
 */
export function SessionQuestions({ questions }: { questions: AdminRoadmapSessionQuestion[] }) {
  const [, setOpenId] = useOpenQuestion();
  const t = useT();
  if (questions.length === 0) {
    return (
      <Text c="dimmed" size="xs">
        {t('question.noQuestions')}
      </Text>
    );
  }
  return (
    <List type="ordered" size="sm" spacing={6}>
      {[...questions]
        .sort((a, b) => a.orderIndex - b.orderIndex)
        .map((q) => (
          <List.Item key={q.questionId}>
            <Anchor
              component="button"
              type="button"
              size="sm"
              ta="left"
              onClick={() => setOpenId(q.questionId)}
              title={t('question.viewDetail')}
            >
              {q.prompt}
            </Anchor>
            <Text c="dimmed" size="xs">
              {q.type} · {q.difficulty}
              {q.confidence != null
                ? ` · ${t('question.confidence', { value: q.confidence })}`
                : ''}
            </Text>
            {(q.answerText || q.answerCode) && (
              <Card withBorder radius="sm" padding="xs" mt={4} bg="var(--mantine-color-default)">
                <Text size="xs" fw={600} c="dimmed" mb={2}>
                  {t('question.answer')}
                </Text>
                {q.answerText && (
                  <Text size="xs" style={{ whiteSpace: 'pre-wrap' }}>
                    {q.answerText}
                  </Text>
                )}
                {q.answerCode && (
                  <pre className="code-block" style={{ marginTop: q.answerText ? 6 : 0 }}>
                    <code>{q.answerCode}</code>
                  </pre>
                )}
              </Card>
            )}
          </List.Item>
        ))}
    </List>
  );
}
